import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuctionByNft, type Auction, type AuctionBid } from '@/api/auctions';

interface State {
  auction: Auction | null;
  bids: AuctionBid[];
  loading: boolean;
}

const POLL_INTERVAL_MS = 15_000;

/**
 * Fetches the active auction (if any) for an NFT and exposes a refresh fn.
 *
 * Polls every 15s while the tab is visible so the page reflects background
 * state changes — settlement firing, another user outbidding, the seller
 * cancelling, etc. Without polling, the UI is stale until manual refresh.
 *
 * Skips polling when the tab is hidden (Page Visibility API). Resumes
 * immediately when the tab becomes visible again, with a fresh fetch.
 */
export function useActiveAuction(nftTokenId: string | null | undefined) {
  const [state, setState] = useState<State>({ auction: null, bids: [], loading: true });
  const timerRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!nftTokenId) {
      setState({ auction: null, bids: [], loading: false });
      return;
    }
    const res = await getAuctionByNft(nftTokenId);
    if (res.success && res.data) {
      setState({ auction: res.data.auction, bids: res.data.bids, loading: false });
    } else {
      setState({ auction: null, bids: [], loading: false });
    }
  }, [nftTokenId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!nftTokenId) return;

    function startPolling() {
      stopPolling();
      timerRef.current = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          void refresh();
        }
      }, POLL_INTERVAL_MS);
    }

    function stopPolling() {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void refresh();
        startPolling();
      } else {
        stopPolling();
      }
    }

    if (document.visibilityState === 'visible') {
      startPolling();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [nftTokenId, refresh]);

  return { ...state, refresh };
}
